import React, { useState, useEffect, useCallback, useRef } from 'react'
import { GoogleMap, InfoWindow, Marker, Polyline, DirectionsRenderer, MarkerClusterer } from '@react-google-maps/api';
import { message } from 'antd';
import mapTypeIcon from '../../assets/images/mapType.png';
import locateMeIcon from '../../assets/images/locate-me.svg';
import CircleMarker from './CircleMarker';
import icon from '../../assets/images/driver-icon.svg';
import activeDriverIcon from '../../assets/images/driver-icon-active.svg';
import DriverInfo from './DriverInfo';
import { useLocation } from 'react-router-dom';
import './Map.css';

const containerStyle = {
  width: '100%',
  height: '100%'
};

const center = {
  lat: 42.84379903120136,
  lng: 74.62316104222083
};

const DEFAULT_MAP_ZOOM = 6;

const isMapDetailPath = (pathname) => {
  if (/^\/transports\/[^/]+\/?$/.test(pathname)) {
    return true;
  }

  const orderDetailMatch = pathname.match(/^\/orders\/([^/]+)\/?$/);
  return Boolean(orderDetailMatch && orderDetailMatch[1] !== 'create');
};


const Map = ({ drivers = [], scrollTo, selected, selectedSearch, showOnMap }) => {
  const [currentLocation, setCurrentLocation] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [map, setMap] = useState(null);
  const [mapType, setMapType] = useState('roadmap'); // 'roadmap' for standard, 'hybrid' for satellite
  const mapTypeRef = useRef('roadmap'); // Ref to track current map type
  const [directions, setDirections] = useState(null);
  const polylines = useRef([]);
  const mapRef = useRef(null);
  const polylineRef = useRef(null);
  const locationControlRef = useRef(null);
  const locationControlClickRef = useRef(null);
  const directionsRequestRef = useRef(0);
  const location = useLocation();
  const previousPathRef = useRef(location.pathname);
  const driverDetailMatch = location.pathname.match(/^\/transports\/([^/]+)\/?$/);
  const driverDetailId = driverDetailMatch ? decodeURIComponent(driverDetailMatch[1]) : null;
  const isDriverDetail = Boolean(driverDetailId);
  const orderDetailMatch = location.pathname.match(/^\/orders\/([^/]+)\/?$/);
  const orderDetailId = orderDetailMatch ? decodeURIComponent(orderDetailMatch[1]) : null;
  const isOrderDetail = Boolean(orderDetailId && orderDetailId !== 'create');
  const detailDriver = isDriverDetail
    ? drivers.find((driver) => String(driver.id) === String(driverDetailId))
    : null;
  const orderDetailDriver = isOrderDetail && showOnMap?.orderDetailDriverId
    ? drivers.find(
      (driver) => String(driver.id) === String(showOnMap.orderDetailDriverId),
    )
    : null;
  const isolatesDrivers = isDriverDetail || isOrderDetail;

  const locateUser = useCallback((mapInstance) => {
    if (!mapInstance) {
      return;
    }

    const locationControl = locationControlRef.current;

    if (!navigator.geolocation) {
      message.error('Геолокация не поддерживается этим браузером');
      return;
    }

    if (locationControl) {
      locationControl.disabled = true;
      locationControl.classList.add('map-location-control--loading');
    }

    const finishLocating = () => {
      if (!locationControlRef.current) {
        return;
      }

      locationControlRef.current.disabled = false;
      locationControlRef.current.classList.remove('map-location-control--loading');
    };

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const nextLocation = {
          lat: coords.latitude,
          lng: coords.longitude,
        };

        setCurrentLocation(nextLocation);
        mapInstance.panTo(nextLocation);
        mapInstance.setZoom(DEFAULT_MAP_ZOOM);
        finishLocating();
      },
      (error) => {
        const errorMessage = error.code === error.PERMISSION_DENIED
          ? 'Разрешите доступ к геолокации в настройках браузера'
          : 'Не удалось определить ваше местоположение';

        message.error(errorMessage);
        finishLocating();
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
      },
    );
  }, []);

  const onLoad = useCallback(function callback(map) {
    setMap(map);

    // Create custom map type control
    const customControlDiv = document.createElement('div');
    customControlDiv.style.display = 'flex';
    customControlDiv.style.alignItems = 'center';
    customControlDiv.style.justifyContent = 'center';
    customControlDiv.style.width = '50px';
    customControlDiv.style.height = '50px';
    customControlDiv.style.border = '2px solid transparent';
    customControlDiv.style.cursor = 'pointer';
    customControlDiv.style.borderRadius = '100%';
    customControlDiv.style.backgroundColor = '#fff';
    customControlDiv.style.marginRight = '7px';
    customControlDiv.style.marginTop = '5px';

    // Create the control button
    const controlButton = document.createElement('img');
    controlButton.src = mapTypeIcon;
    controlButton.style.width = '30px';
    controlButton.style.height = '30px';
    controlButton.style.borderRadius = '2px';
    controlButton.title = 'Click to change map type';
    customControlDiv.appendChild(controlButton);

    // Setup click event
    customControlDiv.addEventListener('click', () => {
      // Read from ref instead of state to always get the current value
      const currentMapType = mapTypeRef.current;

      const newMapType = currentMapType === 'roadmap' ? 'hybrid' : 'roadmap';

      // Update border based on new map type
      if (newMapType === 'hybrid') {
        customControlDiv.style.border = '2px solid #2F80ED';
      } else {
        customControlDiv.style.border = '2px solid transparent';
      }

      // Update both state and ref
      mapTypeRef.current = newMapType;
      setMapType(newMapType);
      map.setMapTypeId(newMapType);
    });

    // Add the custom control to the map
    map.controls[window.google.maps.ControlPosition.TOP_RIGHT].push(customControlDiv);

    const locationControl = document.createElement('button');
    locationControl.type = 'button';
    locationControl.className = 'map-location-control';
    locationControl.title = 'Показать моё местоположение';
    locationControl.setAttribute('aria-label', 'Показать моё местоположение');
    const locationIcon = document.createElement('img');
    locationIcon.src = locateMeIcon;
    locationIcon.alt = '';
    locationIcon.setAttribute('aria-hidden', 'true');
    locationControl.appendChild(locationIcon);

    const handleLocateClick = () => locateUser(map);

    locationControlRef.current = locationControl;
    locationControlClickRef.current = handleLocateClick;
    locationControl.addEventListener('click', handleLocateClick);
    map.controls[window.google.maps.ControlPosition.TOP_RIGHT].push(locationControl);
  }, [locateUser])

  const onUnmount = React.useCallback(function callback() {
    if (locationControlRef.current && locationControlClickRef.current) {
      locationControlRef.current.removeEventListener(
        'click',
        locationControlClickRef.current,
      );
    }

    locationControlRef.current = null;
    locationControlClickRef.current = null;
    setMap(null)
  }, [])

  // Update ref when state changes
  useEffect(() => {
    mapTypeRef.current = mapType;
  }, [mapType]);

  useEffect(() => {
    if (map && location.pathname.includes('/transports/')) {
      map.setZoom(13);
    } else if (map && !location.pathname.includes('/transports/')) {
      map.setZoom(DEFAULT_MAP_ZOOM);
    }
  }, [location.pathname, map]);

  useEffect(() => {
    const previousPath = previousPathRef.current;
    const returnedFromDetail =
      isMapDetailPath(previousPath) && !isMapDetailPath(location.pathname);

    previousPathRef.current = location.pathname;

    if (!map || !returnedFromDetail) {
      return;
    }

    setSelectedDriver(null);
    setDirections(null);
    polylines.current = [];
    directionsRequestRef.current += 1;

    if (currentLocation) {
      map.panTo(currentLocation);
      map.setZoom(DEFAULT_MAP_ZOOM);
      return;
    }

    locateUser(map);
  }, [currentLocation, locateUser, location.pathname, map]);

  useEffect(() => {
    if (!map || !isDriverDetail || !detailDriver) {
      return;
    }

    const driverPosition = {
      lat: Number(detailDriver.latitude),
      lng: Number(detailDriver.longitude),
    };

    if (!Number.isFinite(driverPosition.lat) || !Number.isFinite(driverPosition.lng)) {
      return;
    }

    setSelectedDriver(null);
    setDirections(null);
    polylines.current = [];
    map.panTo(driverPosition);
    map.setZoom(15);
  }, [detailDriver, isDriverDetail, map]);

  useEffect(() => {
    if (scrollTo) {
      map.panTo(new window.google.maps.LatLng(scrollTo.latitude, scrollTo.longitude));
    }
  }, [scrollTo, map])

  const setPolyline = (polyline, from, to) => {
    const decodedPolyline = decode(polyline);

    // setDirections(decodedPolyline);
    polylines.current = decodedPolyline;

    const origin = new window.google.maps.LatLng(from.lat, from.lng);
    const destination = new window.google.maps.LatLng(to.lat, to.lng);
    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend(origin);
    bounds.extend(destination);
    map.fitBounds(bounds);
  }

  useEffect(() => {
    const hasOrderDetailMapState = Boolean(
      showOnMap &&
      Object.prototype.hasOwnProperty.call(showOnMap, 'orderDetailDriverId'),
    );

    if (!isOrderDetail && hasOrderDetailMapState) {
      directionsRequestRef.current += 1;
      setDirections(null);
      polylines.current = [];
      return;
    }

    if (isDriverDetail) {
      directionsRequestRef.current += 1;
      setDirections(null);
      polylines.current = [];
      return;
    }

    if (showOnMap && map) {
      if (showOnMap.order) {
        polylines.current = [];
        const { from, to, route_polyline } = showOnMap.order;
        if (route_polyline) {
          setDirections(null);
          setPolyline(route_polyline, from, to);
        } else {
          polylines.current = [];
          const origin = new window.google.maps.LatLng(from.lat, from.lng);
          const destination = new window.google.maps.LatLng(to.lat, to.lng);

          const directionsService = new window.google.maps.DirectionsService();
          const requestId = directionsRequestRef.current + 1;
          directionsRequestRef.current = requestId;

          directionsService.route(
            {
              origin,
              destination,
              travelMode: window.google.maps.TravelMode.DRIVING,
            },
            (result, status) => {
              if (requestId !== directionsRequestRef.current) {
                return;
              }

              if (status === window.google.maps.DirectionsStatus.OK) {
                setDirections(result);

                // Pan to driver if exists, otherwise fit bounds to include the route
                const bounds = new window.google.maps.LatLngBounds();
                bounds.extend(origin);
                bounds.extend(destination);
                map.fitBounds(bounds);
              } else {
                console.error('Error fetching directions:', status);
              }
            }
          );
        }
      } else if (showOnMap.driver) {
        // If only driver exists (no order), pan to driver
        map.panTo(new window.google.maps.LatLng(showOnMap.driver.lat, showOnMap.driver.lng));
        polylines.current = [];
        setDirections(null);
      }
    } else if (showOnMap === null) {
      directionsRequestRef.current += 1;
      setDirections(null);
      polylines.current = [];
    }
  }, [showOnMap, map, isDriverDetail, isOrderDetail])

  // Handler for marker click
  const handleMarkerClick = (driver) => {
    if (driver.id !== selectedDriver?.id) {
      map.panTo(new window.google.maps.LatLng(driver.latitude, driver.longitude));
      setSelectedDriver(driver);
    }
  };

  const onLoadPolyline = polyline => {
    console.log('onLoadPolyline: ', polyline)
  };

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={currentLocation || center}
      zoom={DEFAULT_MAP_ZOOM}
      onLoad={onLoad}
      onUnmount={onUnmount}
      mapTypeId={mapType}
      ref={mapRef}
      options={{
        mapTypeControl: false, // Remove default map type controls
        fullscreenControl: true,
        streetViewControl: true,
        zoomControl: true
      }}
    >
      {isolatesDrivers ? (
        (detailDriver || orderDetailDriver) && (
          <CircleMarker
            key={`${isDriverDetail ? 'driver' : 'order'}-${(detailDriver || orderDetailDriver).id}`}
            position={{
              lat: Number((detailDriver || orderDetailDriver).latitude),
              lng: Number((detailDriver || orderDetailDriver).longitude),
            }}
            image={activeDriverIcon}
            size={58}
            highlighted
          />
        )
      ) : (
        <MarkerClusterer maxZoom={12}>
          {(clusterer) =>
            drivers.length > 0 && drivers.map((driver) => (
              <CircleMarker
                onClick={() => handleMarkerClick(driver)}
                key={driver.id}
                position={{ lat: driver.latitude, lng: driver.longitude }}
                image={icon}
                size={50}
                clusterer={clusterer}
              />
            ))
          }
        </MarkerClusterer>
      )}

      {currentLocation && !isolatesDrivers && (
        <Marker
          position={currentLocation}
          clickable={false}
          zIndex={1001}
          icon={{
            path: window.google.maps.SymbolPath.CIRCLE,
            fillColor: '#2F80ED',
            fillOpacity: 1,
            strokeColor: '#FFFFFF',
            strokeWeight: 3,
            scale: 8,
          }}
        />
      )}
      {
        (!isDriverDetail && showOnMap && showOnMap.driver) && (
          <CircleMarker
            position={{ lat: showOnMap.driver.lat, lng: showOnMap.driver.lng }}
            image={icon}
            size={50}
          />
        )
      }

      {/* Render order markers and directions */}
      {(!isDriverDetail && showOnMap && showOnMap.order) && (
        <>
          <Polyline
            ref={polylineRef}
            path={polylines.current || []}
            onLoad={onLoadPolyline}
            visible={true}
            // geodesic={true}
            options={{
              strokeColor: "#2F80ED",
              strokeWeight: 4,
              strokeOpacity: 1,
              zIndex: 999,
              paths: directions
            }}
          />
          {/* Pickup location marker */}
          <Marker
            position={{ lat: showOnMap.order.from.lat, lng: showOnMap.order.from.lng }}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              fillColor: "#1677FF", // Blue color
              fillOpacity: 1,
              strokeWeight: 0,
              scale: 12,
            }}
            label={{
              text: "A",
              color: "white",
              fontWeight: "600"
            }}
          />

          {/* Delivery location marker */}
          <Marker
            position={{ lat: showOnMap.order.to.lat, lng: showOnMap.order.to.lng }}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              fillColor: "#EB5757", // Blue color
              fillOpacity: 1,
              strokeWeight: 0,
              scale: 12,
            }}
            label={{
              text: "B",
              color: "white",
              fontWeight: "600"
            }}
          />

          {/* Render directions */}
          {(directions && showOnMap) && (
            <DirectionsRenderer
              directions={directions}
              options={{
                suppressMarkers: true, // Don't show default A, B markers
                polylineOptions: {
                  strokeColor: "#2F80ED",
                  strokeWeight: 4
                }
              }}
            />
          )}
        </>
      )}

      {selectedDriver && !isolatesDrivers && (
        <InfoWindow
          key={selectedDriver.id}
          position={{ lat: selectedDriver.latitude, lng: selectedDriver.longitude }}
          onCloseClick={() => setSelectedDriver(null)}
          options={{ pixelOffset: new window.google.maps.Size(0, -20), position: { lat: selectedDriver.latitude, lng: selectedDriver.longitude } }}
        >
          <div className="driver-info-container">
            <DriverInfo _driver={selectedDriver} />
          </div>
        </InfoWindow>
      )}

    </GoogleMap>
  )
}

// eslint-disable-next-line no-unused-expressions
const decode = (t, e) => {
  for (var n, o, u = 0, l = 0, r = 0, d = [], h = 0, i = 0, a = null, c = Math.pow(10, e || 5); u < t.length;) {
    // eslint-disable-next-line no-unused-expressions
    a = null, h = 0, i = 0;
    // eslint-disable-next-line no-unused-expressions
    do a = t.charCodeAt(u++) - 63, i |= (31 & a) << h, h += 5; while (a >= 32);
    // eslint-disable-next-line no-unused-expressions
    n = 1 & i ? ~(i >> 1) : i >> 1, h = i = 0;
    // eslint-disable-next-line no-unused-expressions
    do a = t.charCodeAt(u++) - 63, i |= (31 & a) << h, h += 5; while (a >= 32);
    // eslint-disable-next-line no-unused-expressions
    o = 1 & i ? ~(i >> 1) : i >> 1, l += n, r += o, d.push([l / c, r / c]);
  }
  return d.map(point => {
    return {
      lat: point[0],
      lng: point[1]
    };
  });
};

export default React.memo(Map)
